import React from 'react'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { StatusToneChip } from 'src/components/partner-driver/shared/StatusToneChip'
import { StepActionsCell } from 'src/components/partner-driver/steps/StepActionsCell'
import type { PartnerDriverStep, StepActionPermissions } from 'src/interfaces/partnerDriver'
import { stepDetails } from 'src/utils/partnerDriver/stepDetails'
import { stepDisplayLabel } from 'src/utils/partnerDriver/stepDisplayLabel'
import { stepStatusTitles, stepStatusTones, stepsTableHeaderTitles } from './constants'
import { humanizeSnakeCase } from 'src/utils/humanizeSnakeCase'
import { StatusTone, StepAction, StepsTableFields } from './enums'

/**
 * A factory: the actions column closes over the card's selection handler and the user's step
 * permissions. Callers memoize it, or the grid's column state resets on every render. Holding
 * neither step permission leaves no menu item to offer, so the actions column is dropped rather
 * than left empty (ENG-1207 part 2).
 */
export const buildStepColumns = (
  onSelectAction: (step: PartnerDriverStep, action: StepAction) => void,
  permissions: StepActionPermissions,
): Array<GridColDef<PartnerDriverStep>> => {
  const columns: Array<GridColDef<PartnerDriverStep>> = [
    {
      field: StepsTableFields.step,
      headerName: stepsTableHeaderTitles[StepsTableFields.step],
      flex: 2,
      minWidth: 240,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<PartnerDriverStep>) => {
        return (
          <div css={tw`flex min-w-0 flex-col py-2`}>
            <Typography variant="body2" css={tw`truncate text-shades-1000`}>
              {stepDisplayLabel(params.row)}
            </Typography>
            <Typography variant="body2" css={tw`truncate font-mono text-xs text-shades-500`}>
              {params.row.id}
            </Typography>
          </div>
        )
      },
    },
    {
      field: StepsTableFields.status,
      headerName: stepsTableHeaderTitles[StepsTableFields.status],
      width: 240,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<PartnerDriverStep>) => {
        const { status } = params.row

        return (
          <div css={tw`flex flex-row items-center gap-2`}>
            <StatusToneChip
              tone={stepStatusTones[status] ?? StatusTone.Neutral}
              text={stepStatusTitles[status] ?? humanizeSnakeCase(status)}
            />
            {params.row.disabled ? (
              <Typography variant="body2" css={tw`text-xs text-shades-500`}>
                Delayed
              </Typography>
            ) : null}
          </div>
        )
      },
    },
    {
      field: StepsTableFields.documents,
      headerName: stepsTableHeaderTitles[StepsTableFields.documents],
      width: 110,
      sortable: false,
      type: 'number',
      display: 'flex',
      valueGetter: (_value: unknown, row: PartnerDriverStep) => {
        return row.partnerDriverCollectedData?.documents?.length ?? 0
      },
    },
    {
      field: StepsTableFields.details,
      headerName: stepsTableHeaderTitles[StepsTableFields.details],
      flex: 2,
      minWidth: 200,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<PartnerDriverStep>) => {
        const details = stepDetails(params.row)

        if (!details) {
          return (
            <Typography variant="body2" css={tw`text-shades-500`}>
              —
            </Typography>
          )
        }

        return (
          <Typography variant="body2" css={tw`truncate`}>
            {details}
          </Typography>
        )
      },
    },
  ]

  if (!permissions.canManageSteps && !permissions.canManageDocuments) {
    return columns
  }

  return [
    ...columns,
    {
      field: StepsTableFields.actions,
      headerName: stepsTableHeaderTitles[StepsTableFields.actions],
      width: 68,
      sortable: false,
      hideable: false,
      disableExport: true,
      align: 'right',
      headerAlign: 'right',
      display: 'flex',
      renderCell: (params: GridRenderCellParams<PartnerDriverStep>) => {
        return (
          <StepActionsCell
            step={params.row}
            permissions={permissions}
            onSelectAction={onSelectAction}
          />
        )
      },
    },
  ]
}
